import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { MatIconButton } from '@angular/material/button';
import { MatMenuTrigger, MatMenu, MatMenuItem } from '@angular/material/menu';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { PieChartModule } from '@swimlane/ngx-charts';
import { TranslocoService } from '@jsverse/transloco';

interface CategoryResultItem {
  difficulty: string;
  name: string;
  value: number;
}

@Component({
  selector: 'app-chart-with-categories',
  templateUrl: './chart-with-categories.component.html',
  styleUrls: ['./chart-with-categories.component.css'],
  imports: [
    MatIconButton,
    MatMenuTrigger,
    MatIcon,
    MatMenu,
    MatMenuItem,
    NgClass,
    PieChartModule,
  ],
})
export class ChartWithCategoriesComponent implements OnInit, OnChanges {
  @Input() chartTitle = 'Logro promedio';
  @Input() resultPercentage: any = [];
  @Input() colorScheme: { domain: string[] } = {
    domain: ['#5072ff', '#bdbdbd'],
  };
  @Input() isFilterOpen = false;
  view: number[] = [150, 150];

  selectedCategory = '';
  selectedCategoryData: any = [];

  filteredAndTransformedData: any[] = [];
  constructor(private transloco: TranslocoService) {}

  ngOnInit(): void {
    this.selectedCategory = this.resultPercentage[0];
    this.updateSelectedCategoryData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.resultPercentage) this.updateSelectedCategoryData();

    if (this.isFilterOpen) this.view = [130, 130];
    else this.view = [150, 150];
  }
  /**
   * Changes the selected category and updates category-related data.
   *
   * @param {string} category - The new selected category.
   */
  changeCategory(category: string) {
    this.selectedCategory = category;
    this.updateSelectedCategoryData();
  }

  /**
   * Updates the selected category data based on resultPercentage.
   *
   * This function filters and updates the selectedCategoryData property.
   */
  updateSelectedCategoryData() {
    this.selectedCategoryData = this.resultPercentage.filter(
      (item: CategoryResultItem) => item.difficulty === this.selectedCategory,
    );

    if (
      this.selectedCategoryData.length === 0 &&
      this.resultPercentage.length > 0
    ) {
      this.selectedCategory = this.resultPercentage[0].difficulty;
      this.selectedCategoryData = this.resultPercentage.filter(
        (item: CategoryResultItem) => item.difficulty === this.selectedCategory,
      );
    }

    this.filteredAndTransformedData = this.filterAndTransformData(
      this.selectedCategory,
    );
  }
  /**
   * Gets filtered items from resultPercentage based on difficulty.
   *
   * @param {string} difficulty - The difficulty level to filter by.
   * @returns {Array} An array of items matching the specified difficulty.
   * If the provided difficulty is an empty string, return the first item in resultPercentage.
   * Otherwise, filter the resultPercentage array to find items with the specified difficulty.
   */
  getFilteredItems(difficulty: string): any[] {
    if (difficulty === '') {
      return this.resultPercentage[0];
    } else {
      return this.resultPercentage.filter(
        (item: CategoryResultItem) => item.difficulty === difficulty,
      );
    }
  }
  /**
   * Returns the percentage for the selected category.
   *
   * @returns {number} The percentage for the selected category.
   * If a matching item is found, return its percentage value; otherwise, return 0.
   */
  selectedCategoryPercentage(): number {
    // Search for an item in resultPercentage that matches the selected category.

    const selectedData = this.resultPercentage.find(
      (item: CategoryResultItem) => item.difficulty === this.selectedCategory,
    );
    return selectedData ? selectedData.value : 0;
  }

  /**
   * Filters and transforms data based on the selected difficulty level.
   *
   * @param {string} selectedDifficulty - The selected difficulty level.
   * @returns {Array} Transformed data for the selected difficulty.
   */
  filterAndTransformData(selectedDifficulty: string) {
    const filteredData = this.resultPercentage.filter(
      (item: CategoryResultItem) => item.difficulty === selectedDifficulty,
    );

    const transformedData = filteredData.map((item: CategoryResultItem) => {
      return [
        {
          name: this.transloco.translate('pages.QUIZ-CHARTS.COMPLETED'),
          value: item.value,
        },
        {
          name: this.transloco.translate('pages.QUIZ-CHARTS.NOT-COMPLETED'),
          value: 100 - item.value,
        },
      ];
    });

    return transformedData.reduce(
      (acc: any[], val: any[]) => acc.concat(val),
      [],
    );
  }
  /**
   * Gets the CSS class for a given category.
   *
   * @param {string} category - The category name.
   * @returns {string} The CSS class name corresponding to the category.
   */
  getCategoryCssClass(category: string): string {
    switch (category) {
      case 'EASY':
        return 'category-chip-color-green';
      case 'HARD':
        return 'category-chip-color-red';
      case 'MEDIUM':
        return 'category-chip-color-orange';
      case 'cientific skill':
        return 'category-chip-color-blue';
      default:
        return 'category-chip-color-blue';
    }
  }

  /**
   * Gets the CSS class for a given category.
   *
   * @param {string} category - The category name.
   * @returns {string} The CSS class name corresponding to the category.
   */
  getCategoryCssInfoClass(category: string): string {
    switch (category) {
      case 'EASY':
        return 'category-info-chip-color-green';
      case 'HARD':
        return 'category-info-chip-color-red';
      case 'MEDIUM':
        return 'category-info-chip-color-orange';
      case 'cientific skill':
        return 'category-info-chip-color-blue';
      default:
        return 'category-info-chip-color-blue';
    }
  }
}
